import { useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/components/LanguageContext";

const testimonials = {
  es: [
    {
      name: "María González",
      company: "Directora, Clínica Dental Sonrisa",
      text: "¡Increíble servicio! Mi clínica nunca había estado tan impecable. El equipo llegó puntual, fue súper profesional y el resultado superó todas mis expectativas. ¡100% recomendado!",
      avatar: "https://i.pravatar.cc/80?img=47",
      stars: 5,
    },
    {
      name: "Carlos Martínez",
      company: "CEO, Oficinas TechHub",
      text: "Llevamos 6 meses con ellos y la diferencia es brutal. Nuestros empleados están más contentos y los clientes siempre comentan lo limpio que está el espacio. Servicio élite.",
      avatar: "https://i.pravatar.cc/80?img=12",
      stars: 5,
    },
    {
      name: "Laura Sánchez",
      company: "Propietaria de Hogar",
      text: "Contraté el servicio de fin de obra y quedé sin palabras. Dejaron el piso como si fuera nuevo. Rápidos, eficientes y muy amables. ¡Ya los tengo contratados para el mantenimiento mensual!",
      avatar: "https://i.pravatar.cc/80?img=32",
      stars: 5,
    },
    {
      name: "Andrés Romero",
      company: "Gerente, Restaurante El Rincón",
      text: "En hostelería la limpieza es crítica. Estos profesionales lo entienden perfectamente. Puntualidad, discreción y resultados impecables. No cambiaría de empresa por nada.",
      avatar: "https://i.pravatar.cc/80?img=68",
      stars: 5,
    },
  ],
  en: [
    {
      name: "María González",
      company: "Director, Sonrisa Dental Clinic",
      text: "Incredible service! My clinic has never been so spotless. The team arrived on time, was super professional, and the results exceeded all my expectations. 100% recommended!",
      avatar: "https://i.pravatar.cc/80?img=47",
      stars: 5,
    },
    {
      name: "Carlos Martínez",
      company: "CEO, TechHub Offices",
      text: "We've been with them for 6 months and the difference is amazing. Our employees are happier and clients always comment on how clean the space is. Elite service.",
      avatar: "https://i.pravatar.cc/80?img=12",
      stars: 5,
    },
    {
      name: "Laura Sánchez",
      company: "Homeowner",
      text: "I hired the post-construction service and was speechless. They left the place looking brand new. Fast, efficient, and very friendly. I've already booked them for monthly maintenance!",
      avatar: "https://i.pravatar.cc/80?img=32",
      stars: 5,
    },
    {
      name: "Andrés Romero",
      company: "Manager, El Rincón Restaurant",
      text: "In hospitality, cleanliness is critical. These professionals understand that perfectly. Punctuality, discretion, and impeccable results. I wouldn't change companies for anything.",
      avatar: "https://i.pravatar.cc/80?img=68",
      stars: 5,
    },
  ],
};

const labels = {
  es: { badge: "Prueba Social", title: "Lo que dicen nuestros clientes", highlight: "impecables", sub: "Más de 40 clientes satisfechos avalan nuestro trabajo. Aquí algunos de sus testimonios." },
  en: { badge: "Social Proof", title: "What our clients say about us", highlight: "spotless results", sub: "Over 40 satisfied clients back our work. Here are some of their testimonials." },
};

export default function Testimonials() {
  const { lang } = useLang();
  const [current, setCurrent] = useState(0);
  const list = testimonials[lang];
  const lx = labels[lang];

  const prev = () => setCurrent((c) => (c === 0 ? list.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === list.length - 1 ? 0 : c + 1));

  return (
    <section className="bg-gray-950 py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-green-400 font-semibold text-sm uppercase tracking-widest">{lx.badge}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">
            {lx.title} <span className="text-green-400">{lx.highlight}</span>
          </h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto">{lx.sub}</p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {list.map((t, i) => (
            <TestimonialCard key={i} t={t} />
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <TestimonialCard t={list[current]} />
          <div className="flex justify-center items-center gap-4 mt-6">
            <button onClick={prev} className="bg-gray-900 border border-gray-700 hover:border-green-500 rounded-full p-2 shadow transition">
              <ChevronLeft className="w-5 h-5 text-green-400" />
            </button>
            <span className="text-sm text-gray-500">{current + 1} / {list.length}</span>
            <button onClick={next} className="bg-gray-900 border border-gray-700 hover:border-green-500 rounded-full p-2 shadow transition">
              <ChevronRight className="w-5 h-5 text-green-400" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  return (
    <div className="bg-gray-900 border border-gray-800 hover:border-green-500/50 rounded-2xl p-6 flex flex-col gap-4 hover:shadow-xl hover:shadow-green-900/20 transition-all duration-200">
      <div className="flex gap-1">
        {Array.from({ length: t.stars }).map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-green-400 text-green-400" />
        ))}
      </div>
      <p className="text-gray-300 text-sm leading-relaxed italic">"{t.text}"</p>
      <div className="flex items-center gap-3 mt-auto pt-4 border-t border-gray-800">
        <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover border-2 border-green-500/40" />
        <div>
          <p className="font-semibold text-white text-sm">{t.name}</p>
          {t.company && <p className="text-xs text-gray-500">{t.company}</p>}
        </div>
      </div>
    </div>
  );
}